import { logger } from '../../lib/logger';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, MapPin, ShieldCheck, Trees, Award, Instagram, Youtube, Facebook, ChevronRight, Linkedin, Play, Mail, FolderOpen, Leaf, Users, FileText, Star, Sparkles, TrendingUp, Calendar, Send, Palmtree, Globe, Banknote, Flame } from 'lucide-react';
import { BRAND, SOCIALS, SHOWCASE_VIDEOS, VERIFICATION_PASS_VIDEOS, TESTIMONIAL_VIDEOS, LOCATIONS } from '../../lib/brand';
import { GLOBAL_STATS, MANIFESTO, RESORT_FINANCIALS, fmtEUR } from '../../lib/financial';
import { api } from '../../lib/api';
import TrustpilotSection from '../../components/TrustpilotSection';
import ConversionBanner from '../../components/ConversionBanner';
import FaqSection from '../../components/FaqSection';
import MobileStickyCTA from '../../components/MobileStickyCTA';
import VslGate from '../../components/VslGate';
import ExitIntentPopup from '../../components/ExitIntentPopup';
import ScrollProgress from '../../components/ScrollProgress';
import TelegramButton from '../../components/TelegramButton';
import WhatsAppButton from '../../components/WhatsAppButton';
import LiveSocialProof from '../../components/LiveSocialProof';
import VisitsCounter from '../../components/VisitsCounter';
import FakeViewersBadge from '../../components/FakeViewersBadge';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import RiskReversalSection from '../../components/RiskReversalSection';
import ResortQuickCompare from '../../components/ResortQuickCompare';
import ResortCompareTable from '../../components/ResortCompareTable';
import FinalCTASection from '../../components/FinalCTASection';
import { useScoringTracker } from '../../hooks/useScoringTracker';
import { useQualification } from '../../contexts/QualificationContext';
import { attachScrollDepthTracker, trackViewContent } from '../../lib/pixel';

// ─── LANDING MODE FEATURE FLAG ──────────────────────────────────────────
// 'persuasive' (default) → video integrato come hero, landing visibile da
//                          subito tramite scroll, CTA principali sbloccate
//                          dopo il 75 % del video (Form 1 popup 30s
//                          obbligatorio per procedere).
// 'gate'                  → comportamento storico: VslGate come overlay
//                          fullscreen che blocca tutto fino al 75 %.
// Rollback con 1 sola riga: cambia il valore qui sotto e redeploy.
const LANDING_MODE = 'persuasive';

export default function PublicLanding() {
  const { t } = useTranslation();
  const [props, setProps] = useState([]);
  const [visitsBySlug, setVisitsBySlug] = useState({}); // ★ 2026-08-11 (Fase 37) — visite ultimi 7gg per slug
  const [activeVideo, setActiveVideo] = useState(null); // showcase + verification
  const [showAllTestimonials, setShowAllTestimonials] = useState(false); // ★ 2026-10-19 — 14 testimonianze, 4 a vista
  const { open: openQualifica } = useQualification();
  // ★ NEW FLOW: "unlocked" = Form 1 compilato (non più 75% video).
  // In persuasive mode `gateUnlocked` viene sincronizzato con `hasForm1`.
  // In gate mode legacy resta come prima per backwards-compat.
  const [gateUnlocked, setGateUnlocked] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (LANDING_MODE === 'persuasive') {
      try { return !!localStorage.getItem('gp_vsl_phase1'); }
      catch (err) { logger.debug('best-effort op failed', err); return false; }
    }
    try {
      return localStorage.getItem('gp_vsl_unlocked') === '1';
    } catch (err) { logger.debug('best-effort op failed', err); return false; }
  });
  // Form 1 submitted state — read once + re-read whenever the unlock flips.
  // Used to soft-lock CTAs that lead to the qualification flow when in
  // persuasive mode: "no Form 1 → non puoi procedere oltre".
  // NB: VslForm1Popup salva un JSON in gp_vsl_phase1 (oggetto con nome,
  // email, etc.), quindi il check è `!!getItem`, non `=== '1'`.
  const [hasForm1, setHasForm1] = useState(() => {
    if (typeof window === 'undefined') return true;
    try { return !!localStorage.getItem('gp_vsl_phase1'); }
    catch (err) { logger.debug('best-effort op failed', err); return false; }
  });
  // Re-read Form 1 status every 1.5 s while in persuasive mode and not yet
  // detected. The VslForm1Popup sets the flag in localStorage and we want
  // the UI to react to it without a full page reload.
  useEffect(() => {
    if (LANDING_MODE !== 'persuasive') return;
    if (hasForm1) {
      // Mirror Form 1 → gateUnlocked in persuasive (single gate now).
      // Defer via timeout(0) to avoid `set-state-in-effect` lint rule
      // (the state update is intentional, just not concurrent-safe sync).
      const id = setTimeout(() => setGateUnlocked(true), 0);
      return () => clearTimeout(id);
    }
    const t = setInterval(() => {
      try {
        if (localStorage.getItem('gp_vsl_phase1')) {
          setHasForm1(true);
          setGateUnlocked(true);
          clearInterval(t);
        }
      } catch (err) { logger.debug('best-effort op failed', err); }
    }, 1500);
    return () => clearInterval(t);
  }, [hasForm1]);

  // Capture the initial deep-link hash (e.g. #qualifica, #documents, #roi,
  // #verification) so we can REPLAY it AFTER the VSL gate is unlocked.
  // Without this, email links like `/?gp_track=email#qualifica` did nothing
  // because the modal opens via context, not via DOM anchors.
  const deepLinkRef = useRef(
    typeof window !== 'undefined'
      ? (window.location.hash || '').replace(/^#/, '').toLowerCase()
      : '',
  );
  const deepLinkHandledRef = useRef(false);

  // Engagement tracking (lead scoring)
  useScoringTracker({ enabled: gateUnlocked });

  useEffect(() => {
    // ★ 2026-08-12 (Fase 44) — Fetch iniziale + polling ogni 45s
    //   → aggiorna disponibilità unità, contatori, prezzi
    //   in tempo reale senza reload della pagina.
    const fetchAll = () => {
      api.get('/public/properties?language=it').then((r) => setProps(r.data)).catch(() => {});
      api.get('/public/visits-stats-all?days=7').then((r) => setVisitsBySlug(r.data?.by_slug || {})).catch(() => {});
    };
    fetchAll();
    const intervalId = setInterval(fetchAll, 45_000);
    // Aggiorna anche quando l'utente torna sulla tab (dopo idle)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchAll();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ── Meta Pixel: page view + scroll depth tracking ─────────────────────
  // Fires ViewContent once on mount, then ScrollDepth25/50/75/90 buckets
  // as the visitor scrolls (each at most once per page-load).
  useEffect(() => {
    trackViewContent({ name: 'landing', category: 'home' });
    const teardown = attachScrollDepthTracker('landing');
    return teardown;
  }, []);

  // ── EMAIL DEEP-LINK HANDLER ───────────────────────────────────────────
  // Runs once the VSL gate is unlocked. Handles hash-based deep links sent
  // from automated emails:
  //   • #qualifica   → opens the qualification modal (CTA "Compila il modulo")
  //   • #documents   → opens qualification (used by "Ricevi i 3 PDF")
  //   • #roi         → scrolls to resorts section + opens qualification
  //   • #verification→ scrolls to the Verification Pass video block
  //   • #resorts     → smooth-scroll to the resorts section (existing anchor)
  // All link sources from emails carry `?gp_track=emailN` so we pass that to
  // the modal as `ctaSource` for funnel attribution.
  useEffect(() => {
    if (!gateUnlocked) return;
    if (deepLinkHandledRef.current) return;
    const hash = deepLinkRef.current;
    if (!hash) return;
    deepLinkHandledRef.current = true;

    // Pick up the gp_track source for attribution (defaults to 'email_deep_link').
    let trackSource = 'email_deep_link';
    try {
      const qs = new URLSearchParams(window.location.search);
      const t = qs.get('gp_track');
      if (t) trackSource = t;
    } catch (err) { logger.debug('best-effort op failed', err); }

    const scrollTo = (selector) => {
      try {
        const el = document.querySelector(selector);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) { logger.debug('best-effort op failed', err); }
    };

    // Small delay lets the page mount + the gate fade-out animation finish.
    // NOTE: we intentionally do NOT clear this timer in a cleanup function
    // because React StrictMode (dev) double-invokes effects and would
    // cancel the timer on the first cleanup — the second pass would then
    // see `deepLinkHandledRef.current === true` and never reschedule.
    setTimeout(() => {
      if (hash === 'qualifica' || hash === 'documents') {
        openQualifica(`${trackSource}_${hash}`);
      } else if (hash === 'roi') {
        scrollTo('#resorts');
        // After scrolling, gently nudge the user to qualify.
        setTimeout(() => openQualifica(`${trackSource}_roi`), 1200);
      } else if (hash === 'verification') {
        scrollTo('#verification-pass');
      } else if (hash === 'resorts') {
        scrollTo('#resorts');
      }
      // Clean the hash so a refresh doesn't replay it (UTM params kept).
      try {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search,
        );
      } catch (err) { logger.debug('best-effort op failed', err); }
    }, 500);
  }, [gateUnlocked, openQualifica]);

  // Wrapper for CTAs that require Form 1 + 75% video unlock to be active.
  // - Before Form 1: button shows a 🔒 hint and on click smooth-scrolls
  //   the visitor to the hero video (where the 30s popup will trigger).
  // - After Form 1 but before 75 %: same scroll-back behaviour, hint says
  //   "Guarda il video al 75%".
  // - After unlock: button works normally (calls the provided handler).
  // Only active in 'persuasive' mode. In 'gate' mode this is a no-op since
  // the user CANNOT see the landing until unlock anyway.
  const ctaCanProceed = (LANDING_MODE !== 'persuasive') || (hasForm1 && gateUnlocked);
  const scrollToHeroVideo = () => {
    try {
      const el = document.querySelector('[data-testid="vsl-gate"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) { logger.debug('best-effort op failed', err); }
  };

  // Guarded wrapper: every "openQualifica" CTA must go through this when
  // in persuasive mode. It enforces the gate (Form 1 + 75% video) by:
  //   1. If Form 1 not done → emit `gp:force-form1` so the VslGate opens
  //      the popup AND scroll the user to the hero video.
  //   2. If Form 1 done but video < 75% → scroll to the hero video so the
  //      visitor sees they need to keep watching (no popup needed).
  //   3. Otherwise → behave exactly like before (open Form 2).
  const openQualificaGuarded = (source) => {
    if (LANDING_MODE !== 'persuasive') {
      openQualifica(source);
      return;
    }
    if (!hasForm1) {
      try { window.dispatchEvent(new CustomEvent('gp:force-form1')); } catch (err) { logger.debug('best-effort op failed', err); }
      scrollToHeroVideo();
      return;
    }
    if (!gateUnlocked) {
      scrollToHeroVideo();
      return;
    }
    openQualifica(source);
  };

  // ═══════════════════════════════════════════════════════════════════
  // ★ TWO-PAGE FLOW (richiesta utente):
  //   Pagina 1: SOLO video + Form 1 popup (no scroll, no altre sezioni)
  //   Pagina 2: TUTTA la landing + Form 2 accessibile via CTA
  // Lo "switch" tra le due pagine è `hasForm1`: appena l'utente compila
  // il Form 1, React re-renderizza con la landing intera.
  // ═══════════════════════════════════════════════════════════════════
  if (LANDING_MODE === 'persuasive' && !hasForm1) {
    return (
      <div
        className="min-h-screen bg-black flex flex-col"
        data-testid="public-page1-video-only"
      >
        <section className="relative w-full bg-black" data-testid="public-hero-video">
          <div className="px-3 sm:px-6 md:px-12 pt-4 md:pt-6 pb-2 flex items-center justify-between max-w-6xl mx-auto">
            <Link to="/" className="flex items-center gap-2 text-white/90">
              <img
                src={BRAND.logo}
                alt="HCF Property"
                className="h-9 md:h-11 w-auto rounded-md"
              />
              <span className="text-[10px] md:text-xs tracking-[0.3em] uppercase opacity-80 hidden sm:inline">
                HCF Property
              </span>
            </Link>
            <a
              href="https://it.trustpilot.com/review/housecashflows.com"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:flex items-center gap-1.5 text-white/80 text-xs hover:text-white"
              data-testid="hero-video-trustpilot-link"
            >
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" />)}
              <span className="ml-1">{t('landing.excellent', 'Eccellente')} · {t('landing.trustpilot', 'Trustpilot')}</span>
            </a>
          </div>
          {/* VslGate embedded — without onUnlocked: lo sblocco a 75% è disabilitato.
              L'UNICO modo per andare avanti è compilare il Form 1 popup. */}
          <VslGate embedded />
          {/* Hint sotto al video */}
          <div className="px-4 md:px-12 pb-8 md:pb-12 text-center">
            <p className="text-white/55 text-xs md:text-sm mt-3 max-w-xl mx-auto leading-relaxed">
              Per accedere alla proposta completa, lascia i tuoi dati nel modulo che apparirà tra pochi secondi.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-1)]">
      {/* Mode 'gate' (legacy): fullscreen overlay. NEW FLOW: il VslGate
          non chiama più onUnlocked — il gate è solo il Form 1 popup. */}
      {LANDING_MODE === 'gate' && (
        <VslGate />
      )}
      {/* Exit-intent popup — fires once when the user tries to leave (desktop mouseout / mobile idle) */}
      <ExitIntentPopup enabled={gateUnlocked} />
      {/* Fixed side scroll progress indicator */}
      <ScrollProgress hidden={LANDING_MODE === 'gate' && !gateUnlocked} />

      {/* ─────────── PERSUASIVE MODE: VSL hero as a section ─────────── */}
      {/* Embedded VSL as the hero section. The Form 1 popup still fires at
          30 s. The "Sblocca CTA" button appears at 75 %, calls onUnlocked
          and scrolls to the unlocked CTA banner below. */}
      {LANDING_MODE === 'persuasive' && (
        <section
          className="relative w-full bg-black"
          data-testid="public-hero-video"
        >
          <div className="px-3 sm:px-6 md:px-12 pt-4 md:pt-6 pb-2 flex items-center justify-between max-w-6xl mx-auto">
            <Link to="/" className="flex items-center gap-2 text-white/90">
              <img
                src={BRAND.logo}
                alt="HCF Property"
                className="h-9 md:h-11 w-auto rounded-md"
              />
              <span className="text-[10px] md:text-xs tracking-[0.3em] uppercase opacity-80 hidden sm:inline">
                HCF Property
              </span>
            </Link>
            <a
              href="https://it.trustpilot.com/review/housecashflows.com"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:flex items-center gap-1.5 text-white/80 text-xs hover:text-white"
              data-testid="hero-video-trustpilot-link"
            >
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" />)}
              <span className="ml-1">{t('landing.excellent', 'Eccellente')} · {t('landing.trustpilot', 'Trustpilot')}</span>
            </a>
          </div>
          <VslGate embedded />
        </section>
      )}

      {/* ─────────── (Reasons strip e Unlocked-CTA-banner rimossi)
          Motivo: il nuovo flusso a 2 pagine non li richiede più. La Pagina 1
          è SOLO video+Form1. Una volta che Form 1 è completato, l'utente
          atterra direttamente sulla landing completa. ─────────── */}

      {/* ─────────── (REASONS-STRIP rimossa)
          Era una sezione "Non vuoi guardare il video?" pensata per nutrire
          chi non guardava il VSL. Con il nuovo flusso a 2 pagine il Form 1
          è OBBLIGATORIO prima di accedere al resto, quindi la reasons-strip
          non serve più (l'utente già accede alla landing completa). ─────────── */}

      {/* Hero — sales-focused: ONE CTA, headline che vende, trust badges */}
      {/* Hidden in persuasive mode (gli utenti già vedono il video hero + reasons strip) */}
      {LANDING_MODE !== 'persuasive' && (
      <section className="relative h-[92vh] min-h-[580px] overflow-hidden gp-noise" data-testid="public-hero">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${BRAND.heroSecondary})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 gp-hero-overlay" />
        <header className="relative z-10 px-4 md:px-12 py-4 md:py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-[hsl(var(--primary-foreground))]">
            <img src={BRAND.logo} alt="HCF Property" className="h-9 md:h-11 w-auto rounded-md" />
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://it.trustpilot.com/review/housecashflows.com" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 text-white/90 text-xs hover:text-white" data-testid="hero-trustpilot-link">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" />)}
              <span className="ml-1">{t('landing.excellent', 'Eccellente')} · {t('landing.trustpilot', 'Trustpilot')}</span>
            </a>
            {/* ★ 2026-08-14 (Fase 55) — Selettore lingua sul header pubblico */}
            <LanguageSwitcher variant="compact" className="bg-white/10 border-white/25 text-white hover:bg-white/20" />
          </div>
        </header>
        <div className="relative z-10 px-4 md:px-12 mt-4 md:mt-12 max-w-3xl">
          <div className="text-[hsl(var(--primary-foreground))]">
            <div className="text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] uppercase opacity-80">
              {t('landing.hero.stats', '3 paesi · {{units}} appartamenti · da {{price}}', { countries: 3, units: GLOBAL_STATS.totalUnits, price: fmtEUR(GLOBAL_STATS.minDeposit) })}
            </div>
            <h1 className="font-serif text-[2.4rem] sm:text-5xl md:text-6xl lg:text-[5.25rem] leading-[0.95] mt-3" data-testid="hero-h1">
              <Trans i18nKey="landing.hero.title.line1" defaults="I 3 resort di lusso" />
              <br />
              <Trans
                i18nKey="landing.hero.title.line2"
                defaults="che <i>puoi possedere</i>."
                components={{ i: <span className="italic text-[var(--gp-gold-soft)]" /> }}
              />
              <br />
              <Trans i18nKey="landing.hero.title.line3" defaults="Non solo prenotare." />
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-lg opacity-90 max-w-xl">
              <Trans
                i18nKey="landing.hero.subtitle"
                values={{ price: fmtEUR(GLOBAL_STATS.minDeposit) }}
                components={{ b: <b /> }}
              />
            </p>
            <div className="mt-6 md:mt-7">
              <div className="mb-4 text-[11px] md:text-xs text-black/85 flex flex-wrap items-center gap-x-3 gap-y-1 bg-white/95 rounded-md px-3 py-2 w-fit">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />{t('landing.hero.badge.noCommitment', 'Nessun impegno · 60 secondi')}</span>
                <span className="hidden md:inline text-black/40">·</span>
                <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{t('landing.hero.badge.brochureIncluded', 'Brochure & financing inclusi')}</span>
                <span className="hidden md:inline text-black/40">·</span>
                <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" />{t('landing.hero.badge.excellentTrustpilot', 'Eccellente su Trustpilot')}</span>
              </div>
              {/* Primary CTA + WhatsApp + Telegram — pulsanti A COLORI con loghi visibili */}
              <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-2.5 max-w-full">
                <Button
                  size="lg"
                  className="h-12 md:h-14 px-5 md:px-7 text-sm md:text-base whitespace-nowrap shadow-2xl w-full md:w-auto"
                  onClick={() => openQualificaGuarded('hero_primary')}
                  data-testid="hero-cta-primary"
                >
                  {t('landing.hero.ctaPrimary', 'Parla con un Senior Associate')} <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-1.5 md:ml-2 shrink-0" />
                </Button>
                <WhatsAppButton
                  variant="solid"
                  size="lg"
                  label="WhatsApp"
                  source="hero"
                  className="!h-12 md:!h-14 !px-5 md:!px-6 !text-sm md:!text-base w-full md:w-auto"
                />
                <TelegramButton
                  variant="solid"
                  size="lg"
                  label="Telegram"
                  source="hero"
                  className="!h-12 md:!h-14 !px-5 md:!px-6 !text-sm md:!text-base w-full md:w-auto"
                />
              </div>
              {/* Secondary mini-CTAs — TUTTI aprono il modulo qualifica */}
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] md:text-xs text-white/70">
                <button
                  type="button"
                  onClick={() => openQualificaGuarded('hero_link_brochure')}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors underline-offset-4 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                  data-testid="hero-link-brochure"
                >
                  <FileText className="w-3.5 h-3.5" /> {t('landing.hero.linkBrochure', 'Ricevi brochure & documenti')}
                </button>
                <span className="text-white/30">·</span>
                <button
                  type="button"
                  onClick={() => openQualificaGuarded('hero_link_call')}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors underline-offset-4 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                  data-testid="hero-link-call"
                >
                  <Calendar className="w-3.5 h-3.5" /> {t('landing.hero.linkCall', 'Prenota una call')}
                </button>
                <span className="text-white/30 hidden sm:inline">·</span>
                <a
                  href="#resorts"
                  className="hidden sm:inline-flex items-center gap-1.5 hover:text-white transition-colors underline-offset-4 hover:underline"
                  data-testid="hero-link-resorts"
                >
                  <MapPin className="w-3.5 h-3.5" /> {t('landing.hero.linkResorts', 'Esplora i 3 resort')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Trustpilot social proof — right after hero per request */}
      <TrustpilotSection />

      {/* Resorts — subito dopo Trustpilot per accelerare la conversione */}
      <section className="gp-section-accent" id="resorts" data-testid="resorts-section">
        <div className="px-4 md:px-12 py-12 md:py-20 max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-5 md:mb-6 flex-wrap gap-3">
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('landing.resortsSection.eyebrow', 'I progetti attivi')}</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))]">{t('landing.resortsSection.title', 'Esplora ogni destinazione')}</h2>
              {/* ★ 2026-08-12 (Fase 44) — Sotto-titolo DINAMICO: calcolato da props */}
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2" data-testid="resorts-summary">
                {(() => {
                  const totalProjects = props.length;
                  const totalUnits = props.reduce((s, p) => s + (p?.apartments?.total || 0), 0);
                  const availUnits = props.reduce((s, p) => s + (p?.apartments?.available || 0), 0);
                  const countries = new Set(props.map((p) => (p.location || '').split(',').pop().trim()).filter(Boolean));
                  return t('landing.resortsSection.subtitle', '{{projects}} {{projectsLabel}} · {{countries}} {{countriesLabel}} · {{units}} appartamenti totali · {{available}} ancora disponibili.', {
                    projects: totalProjects,
                    projectsLabel: totalProjects === 1 ? t('landing.resortsSection.project', 'progetto') : t('landing.resortsSection.projects', 'progetti'),
                    countries: countries.size,
                    countriesLabel: countries.size === 1 ? t('landing.resortsSection.country', 'paese') : t('landing.resortsSection.countries', 'paesi'),
                    units: totalUnits,
                    available: availUnits,
                  });
                })()}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {props.map((p) => {
              const fin = RESORT_FINANCIALS[p.slug];
              const blocks = fin?.blocks || {};
              const units = fin?.units || { total: null, available: null };
              const isComingSoon = fin?.comingSoon;
              const blocksAvail = blocks.available;
              const unitsAvail = units.available;
              const reAvailable = fin?.realEstate?.available;
              return (
                <div
                  key={p.id}
                  className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card hover:shadow-xl transition-all"
                  data-testid={`resort-card-${p.slug}`}
                >
                  <Link to={`/resort/${p.slug}`} className="block">
                    <div className="aspect-[4/3] w-full relative overflow-hidden">
                      <img src={p.hero_image || p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
                        <span className="text-[10px] tracking-widest uppercase bg-white/95 text-[hsl(var(--primary))] rounded-full px-2.5 py-1 font-medium">{fin?.flag} {p.location?.split(',')[1]?.trim() || fin?.country}</span>
                        {isComingSoon ? (
                          <span className="text-[10px] tracking-widest uppercase bg-[var(--gp-gold)] text-[hsl(var(--primary))] rounded-full px-2.5 py-1 font-bold">{t('landing.card.comingSoon', 'Coming Soon')}</span>
                        ) : fin?.promo?.active ? (
                          <span className="text-[10px] tracking-widest uppercase bg-[var(--gp-gold)] text-[hsl(var(--primary))] rounded-full px-2.5 py-1 font-bold shadow-lg" data-testid={`badge-promo-${p.slug}`}>{t('landing.card.prelaunchDiscount', '-{{pct}}% pre-launch', { pct: fin.promo.discountPct })}</span>
                        ) : unitsAvail === 0 ? (
                          <span className="text-[10px] tracking-widest uppercase bg-red-500/90 text-white rounded-full px-2.5 py-1 font-bold">{t('landing.card.soldOut', 'Sold Out')}</span>
                        ) : unitsAvail != null && units.total && (unitsAvail <= 10 || unitsAvail / units.total <= 0.2) ? (
                          /* ★ 2026-08-29 — Badge urgenza animato "Solo X rimasti" (richiesta utente) */
                          <span
                            className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase bg-red-600/95 text-white rounded-full px-2.5 py-1 font-bold shadow-lg animate-pulse"
                            data-testid={`badge-low-stock-${p.slug}`}
                          >
                            <Flame className="w-3 h-3" />
                            {t('landing.card.onlyLeft', 'Solo {{count}} rimasti', { count: unitsAvail })}
                          </span>
                        ) : null}
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 text-white">
                        <div className="text-[11px] tracking-widest uppercase opacity-80 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</div>
                        <div className="font-serif text-2xl md:text-3xl mt-0.5">{p.short_name || p.name}</div>
                      </div>
                    </div>
                  </Link>
                  <div className="p-4 md:p-5 space-y-3 flex-1 flex flex-col">
                    <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">{p.tagline}</p>

                    {/* KPI badges — grande enfasi su "N disponibili" per creare urgenza */}
                    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5" data-testid={`card-avail-${p.slug}`}>
                        <div className="text-[9px] uppercase tracking-wider text-emerald-700">{t('landing.card.availableNow', 'Disponibili ora')}</div>
                        <div className="font-serif text-lg text-emerald-800 tabular-nums leading-tight">
                          {unitsAvail != null ? (
                            <>
                              {unitsAvail}<span className="text-[11px] text-emerald-600">/{units.total}</span>
                            </>
                          ) : '—'}
                        </div>
                      </div>
                      <div className="rounded-md bg-[var(--surface-2)] border border-border px-2 py-1.5">
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{t('landing.card.startingFrom', 'A partire da')}</div>
                        <div className="font-medium text-foreground">{fin?.realEstate?.startingFrom ? fmtEUR(fin.realEstate.startingFrom) : '—'}</div>
                      </div>
                    </div>
                    {blocks.perBlock && (
                      <div className="text-[10px] text-muted-foreground italic">{blocks.perBlock}</div>
                    )}

                    {/* ★ 2026-08-14 (Fase 52) — Finto contatore visitatori per creare urgenza/scarsità.
                        Sostituisce VisitsCounter (che non si aggiornava in real-time). */}
                    {!isComingSoon && unitsAvail > 0 && (
                      <FakeViewersBadge slug={p.slug} variant="compact" />
                    )}

                    {/* Single prominent CTA: Calcola i numeri della suite */}
                    <div className="mt-auto pt-2">
                      <Link to={`/resort/${p.slug}#calc`} className="contents">
                        <Button
                          variant="default"
                          size="lg"
                          className="text-sm w-full h-11 font-semibold shadow-md hover:shadow-lg transition-shadow"
                          data-testid={`card-cta-calc-${p.slug}`}
                        >
                          <TrendingUp className="w-4 h-4 mr-2" />
                          {t('landing.card.calcNumbers', 'Calcola i tuoi numeri')}
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ★ 2026-08-14 (Fase 52) — LiveSocialProof rimosso: i contatori "Investitori
          a bordo / Brochure scaricate / Call prenotate" non si aggiornavano in
          real-time (dati statici) e generavano più confusione che conversione.
          Sostituiti dal <FakeViewersBadge /> per-card che crea urgenza reale. */}
      {false && <LiveSocialProof />}

      {/* Manifesto */}
      {/* Manifesto — hidden in persuasive (you have the reasons strip serving the same purpose) */}
      {LANDING_MODE !== 'persuasive' && (
      <section className="px-4 md:px-12 py-12 md:py-20 max-w-5xl mx-auto" data-testid="landing-manifesto" data-track-section="benefits">
        <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">Il manifesto</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-2 leading-tight">{MANIFESTO.headline}</h2>
        <p className="mt-3 text-base md:text-xl text-muted-foreground font-serif italic">{MANIFESTO.subheadline}</p>
        <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {MANIFESTO.pillars.map((pl, i) => (
            <div key={pl.title || `pillar-${i}`} className="p-4 md:p-5 rounded-2xl border border-border bg-[var(--surface-2)]">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent))] grid place-items-center border border-[hsl(var(--primary))]/20 shrink-0">
                  {[Leaf, ShieldCheck, Users, Award][i % 4] && React.createElement([Leaf, ShieldCheck, Users, Award][i % 4], { className: 'w-4 h-4 text-[hsl(var(--primary))]' })}
                </div>
                <div>
                  <div className="font-medium text-sm">{pl.title}</div>
                  <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{pl.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-sm italic text-[hsl(var(--primary))] border-l-2 border-[var(--gp-gold)] pl-4">
          {MANIFESTO.claim}
        </div>
      </section>
      )}

      {/* Conversion banner #1 — hidden in persuasive (only #2 docs is kept) */}
      {LANDING_MODE !== 'persuasive' && (
      <ConversionBanner
        eyebrow="Pronto a entrare?"
        title="In 60 secondi sai quale suite fa per te."
        body="Il modulo qualifica il tuo profilo (capitale, orizzonte, paese) e ti propone il resort e la tipologia di appartamento con la simulazione personalizzata. Zero impegno."
        ctaLabel="Compila il modulo"
        onClick={() => openQualificaGuarded('banner_post_manifesto')}
        variant="gold"
        testid="conv-banner-1"
      />
      )}
      {/* ★ 2026-08-13 (Fase 51) — Sezione "Due percorsi" (Equity vs Real Estate) RIMOSSA
          per compliance CONSOB. Sostituita da un'unica proposta Real Estate.
          Il testo originale è conservato in changelog. */}
      <section
        className="px-4 md:px-12 py-12 md:py-16 max-w-6xl mx-auto"
        data-testid="real-estate-section"
        data-track-section="thesis"
      >
        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('landing.howItWorks.eyebrow', 'Come funziona')}</div>
        <h2 className="font-serif text-3xl md:text-5xl text-[hsl(var(--primary))]">{t('landing.howItWorks.title', 'Compra un appartamento in un resort operativo.')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          <div className="p-6 rounded-2xl border border-border bg-[var(--surface-1)]">
            <div className="text-[11px] uppercase tracking-wide text-[var(--gp-teal)]">{t('landing.howItWorks.howBuy', 'Come acquisti')}</div>
            <div className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">{t('landing.howItWorks.installment', 'Piano rateale')}</div>
            <p className="text-sm text-muted-foreground mt-2">{t('landing.howItWorks.installmentDesc', 'Caparra + anticipo, poi rate mensili fino alla consegna. Nessun mutuo, nessuna banca: il pagamento avviene direttamente al developer con contratto notarile.')}</p>
            <ul className="text-sm space-y-1 mt-4">
              <li>• <b>La Mar Residence</b> (Albania): 21 unità · {fmtEUR(RESORT_FINANCIALS['la-mar-residence'].realEstate.startingFrom)}</li>
              <li>• <b>Narea</b> (Albania): 161 unità · 14 piani · {fmtEUR(RESORT_FINANCIALS['narea'].realEstate.startingFrom)}</li>
              <li>• <b>Palm Paradise</b> (Albania): 198 unità · 4 blocchi · {fmtEUR(RESORT_FINANCIALS['palm-paradise'].realEstate.startingFrom)}</li>
              <li>• <b>Vista Mare</b> (Albania): 189 unità · 7 piani · {fmtEUR(RESORT_FINANCIALS['vista-mare'].realEstate.startingFrom)}</li>
              <li>• <b>Green Paradise Lombok</b> (Indonesia): 32 ville · {fmtEUR(RESORT_FINANCIALS['green-paradise-lombok'].realEstate.startingFrom)}</li>
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-border bg-[var(--surface-1)]">
            <div className="text-[11px] uppercase tracking-wide text-[var(--gp-gold)]">{t('landing.howItWorks.howUse', 'Come lo usi')}</div>
            <div className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">{t('landing.howItWorks.incomeOrVacation', 'Rendita o vacanza')}</div>
            <p className="text-sm text-muted-foreground mt-2">{t('landing.howItWorks.incomeOrVacationDesc', "Property management completo Green Paradise: metti l'appartamento a reddito tramite affitto turistico gestito da noi, oppure lo usi per la tua famiglia. Accesso a tutti i servizi del resort.")}</p>
            <ul className="text-sm space-y-1 mt-4">
              <li>• {t('landing.howItWorks.point1', 'Contratto di proprietà notarile')}</li>
              <li>• {t('landing.howItWorks.point2', 'Property management chiavi-in-mano')}</li>
              <li>• {t('landing.howItWorks.point3', 'Rendimento medio target 5–7% netto/anno')}</li>
              <li>• {t('landing.howItWorks.point4', 'Uso personale flessibile')}</li>
            </ul>
            <div className="mt-3 text-[11px] text-muted-foreground">{t('landing.howItWorks.legalNote', 'Contratto compravendita immobiliare · No promessa di rendimento garantito · Consegna 2027-2029.')}</div>
          </div>
        </div>
      </section>

      {/* ★ 2026-08-13 (Fase 51) — Confronto rapido dei progetti attivi (La Mar + Narea) */}
      <ResortQuickCompare />

      {/* Conversion banner #2 — between two paths and showcase videos */}
      <ConversionBanner
        eyebrow={t('landing.banner.compare3.eyebrow', 'Confronta i progetti Albania')}
        title={t('landing.banner.compare3.title', 'Vuoi le brochure ufficiali in un solo PDF?')}
        body={t('landing.banner.compare3.body', 'Ricevi via email le brochure ufficiali di La Mar Residence e Narea + i project financing con KPI completi. Direttamente, senza passaggi intermedi.')}
        ctaLabel={t('landing.banner.compare3.cta', 'Ricevi i PDF')}
        onClick={() => openQualificaGuarded('banner_3pdf')}
        variant="teal"
        testid="conv-banner-2"
      />

      {/* Resort comparison table — fast decision-making for undecided visitors */}
      <ResortCompareTable activeSlugs={props.map((p) => p.slug)} />

      {/* Showcase Videos: Verification Pass + Dicono di noi + Other */}
      <section className="px-4 md:px-12 py-12 md:py-16 max-w-6xl mx-auto" data-testid="showcase-section" data-track-section="media">
        {/* 1) Verification Pass — hidden in persuasive (we keep only "Dicono di noi") */}
        {LANDING_MODE !== 'persuasive' && (
        <div id="verification-pass" data-testid="verification-pass-block">
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('landing.verification.eyebrow', 'Verification Pass')}</div>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-1 leading-tight">{t('landing.verification.title', 'Visto sul posto, non solo in brochure')}</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl">{t('landing.verification.subtitle', 'Il nostro team va personalmente in Albania e Indonesia per verificare ogni progetto. Guarda i video reali del Verification Pass.')}</p>
          <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {VERIFICATION_PASS_VIDEOS.map((v) => (
              <button
                key={v.id}
                onClick={() => setActiveVideo(v)}
                className="group relative aspect-video rounded-xl overflow-hidden border border-border bg-black hover:shadow-xl transition-shadow"
                data-testid={`verification-${v.country}`}
              >
                <img
                  src={`https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 grid place-items-center group-hover:scale-110 transition-transform shadow-2xl">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-[hsl(var(--primary))] ml-1" />
                  </div>
                </div>
                <div className="absolute top-3 left-3 right-3 flex items-center gap-2 text-white">
                  <span className="text-2xl">{v.flag}</span>
                  <span className="text-[10px] tracking-widest uppercase bg-white/20 backdrop-blur rounded-full px-2 py-1">{v.country}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                  <div className="text-[10px] md:text-[11px] tracking-widest uppercase opacity-80">{t('landing.verification.label', 'Verification Pass')}</div>
                  <div className="text-sm md:text-base font-medium">{v.title}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* 2) Dicono di noi — Testimonials (always shown) */}
        <div className={LANDING_MODE === 'persuasive' ? '' : 'mt-12 md:mt-16'} data-testid="testimonials-block">
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('landing.testimonials.eyebrow', 'Dicono di noi')}</div>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 leading-tight">{t('landing.testimonials.title', 'La voce dei nostri investitori')}</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl">{t('landing.testimonials.subtitle', 'Conversazioni reali con chi ha già scelto HCF Property. Niente attori, niente filtri.')}</p>
          <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-testid="testimonials-grid">
            {(showAllTestimonials ? TESTIMONIAL_VIDEOS : TESTIMONIAL_VIDEOS.slice(0, 4)).map((v, i) => (
              <button
                key={v.id}
                onClick={() => setActiveVideo(v)}
                className="group relative aspect-video rounded-xl overflow-hidden border border-border bg-black hover:shadow-xl transition-shadow"
                data-testid={`testimonial-${i}`}
              >
                <img
                  src={`https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 grid place-items-center group-hover:scale-110 transition-transform shadow-2xl">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-[hsl(var(--primary))] ml-1" />
                  </div>
                </div>
                <div className="absolute top-3 left-3 flex items-center gap-1.5 text-white">
                  <Star className="w-3.5 h-3.5 fill-[var(--gp-gold)] text-[var(--gp-gold)]" />
                  <Star className="w-3.5 h-3.5 fill-[var(--gp-gold)] text-[var(--gp-gold)]" />
                  <Star className="w-3.5 h-3.5 fill-[var(--gp-gold)] text-[var(--gp-gold)]" />
                  <Star className="w-3.5 h-3.5 fill-[var(--gp-gold)] text-[var(--gp-gold)]" />
                  <Star className="w-3.5 h-3.5 fill-[var(--gp-gold)] text-[var(--gp-gold)]" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                  <div className="text-[10px] md:text-[11px] tracking-widest uppercase opacity-80">{v.name || t('landing.testimonials.badge', 'Investitore HCF Property')}</div>
                  <div className="text-sm md:text-base font-medium">{v.title}</div>
                </div>
              </button>
            ))}
          </div>
          {/* ★ 2026-10-19 — altre testimonianze (espandibili) */}
          {!showAllTestimonials && TESTIMONIAL_VIDEOS.length > 4 && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setShowAllTestimonials(true)}
                className="rounded-full border-[var(--gp-gold)]/60 hover:bg-[var(--gp-gold)]/10"
                data-testid="testimonials-show-more"
              >
                <Play className="w-4 h-4 mr-2 text-[var(--gp-gold)]" />
                {t('landing.testimonials.showMore', 'Guarda altre {{count}} testimonianze', { count: TESTIMONIAL_VIDEOS.length - 4 })}
              </Button>
            </div>
          )}
          {/* CTA sotto le testimonianze */}
          <div className="mt-6 md:mt-8 rounded-2xl border border-[var(--gp-gold)]/40 bg-gradient-to-br from-[var(--gp-gold)]/15 to-[var(--gp-gold-soft)]/15 p-5 md:p-7" data-testid="testimonials-cta">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('landing.testimonials.cta.eyebrow', 'Anche tu vuoi raccontare la tua?')}</div>
                <h3 className="font-serif text-xl md:text-2xl lg:text-3xl text-[hsl(var(--primary))] mt-1 leading-tight">{t('landing.testimonials.cta.title', 'Diventa il prossimo investitore HCF Property.')}</h3>
                <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-2xl">{t('landing.testimonials.cta.body', 'In 60 secondi capisci quale resort e quale appartamento fa per te. Zero impegno, brochure ufficiali incluse, una call 1:1 con il team se vuoi approfondire.')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0 w-full md:w-auto">
                <Button
                  size="lg"
                  className="text-sm md:text-base whitespace-nowrap"
                  onClick={() => openQualificaGuarded('testimonials_modulo')}
                  data-testid="testimonials-cta-modulo"
                >
                  {t('landing.testimonials.cta.form', 'Compila il modulo')} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  className="text-sm md:text-base whitespace-nowrap"
                  onClick={() => openQualificaGuarded('testimonials_call')}
                  data-testid="testimonials-cta-call"
                >
                  <Calendar className="w-4 h-4 mr-1.5" /> {t('landing.testimonials.cta.call', 'Prenota call')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk reversal — defuse last-minute objections before the final form */}
      <RiskReversalSection />

      {/* Final CTA — hidden in persuasive (footer + Le nostre sedi è la chiusura) */}
      <FinalCTASection />

      {/* Footer */}
      <footer className="px-6 md:px-12 py-12 border-t border-border bg-[var(--surface-1)]">
        {/* I nostri uffici e cantieri — physical presence proofs */}
        <div className="max-w-6xl mx-auto pb-10 mb-10 border-b border-border">
          <div className="text-center mb-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('landing.footer.locations.eyebrow', 'Le nostre sedi')}</div>
            <h3 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] mt-1">{t('landing.footer.locations.title', 'Uffici e cantieri')}</h3>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
              {t('landing.footer.locations.subtitle', 'Una struttura operativa reale: uffici fisici in Europa e cantieri attivi nei tre paesi del network.')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto" data-testid="footer-locations">
            {Object.entries(LOCATIONS).map(([key, loc]) => {
              const TypeBadge = {
                hq: { label: t('landing.footer.locations.hq', 'Sede legale'), color: 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]' },
                office: { label: t('landing.footer.locations.office', 'Ufficio'), color: 'bg-[var(--gp-gold)]/15 text-[var(--gp-forest)]' },
                site: { label: t('landing.footer.locations.site', 'Cantiere attivo'), color: 'bg-emerald-100 text-emerald-800' },
              }[loc.type] || { label: '—', color: 'bg-muted' };
              const Card = loc.maps_url ? 'a' : 'div';
              const cardProps = loc.maps_url
                ? { href: loc.maps_url, target: '_blank', rel: 'noreferrer' }
                : {};
              return (
                <Card
                  key={key}
                  {...cardProps}
                  className="group relative block p-4 rounded-lg border border-border bg-background hover:border-[var(--gp-gold)]/50 hover:shadow-sm transition-all"
                  data-testid={`location-${key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl leading-none" aria-hidden="true">{loc.flag}</span>
                    <span className={`text-[9px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${TypeBadge.color}`}>
                      {TypeBadge.label}
                    </span>
                  </div>
                  <div className="font-serif text-sm text-[hsl(var(--primary))] leading-tight">{loc.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{loc.city}</div>
                  <div className="text-[11px] text-muted-foreground/80 mt-0.5">{loc.address}</div>
                  {loc.maps_url && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-[var(--gp-gold)] group-hover:underline">
                      <MapPin className="w-3 h-3" /> {t('landing.footer.locations.openInMaps', 'Apri su Google Maps')}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="HCF Property" className="h-12 w-auto rounded-md" />
              <div>
                <div className="font-serif text-base text-[hsl(var(--primary))]">HCF Property</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{t('landing.footer.brand.tagline', 'Community Real Estate internazionale')}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-xs">{t('landing.footer.address', 'Bulgaria, Sofia, Stolichna 1797.')}</p>
            <a href={`mailto:${SOCIALS.email}`} className="mt-2 inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:opacity-80" data-testid="footer-email">
              <Mail className="w-3 h-3" /> {SOCIALS.email}
            </a>
          </div>

          <div className="text-xs space-y-1.5">
            <div className="font-medium text-foreground mb-2 uppercase tracking-wider text-[10px]">{t('landing.footer.section.projects', 'Progetti')}</div>
            {/* ★ 2026-08-12 (Fase 44) — Lista progetti DINAMICA (da props fetched) */}
            {props.map((p) => (
              <Link
                key={`footer-${p.slug}`}
                className="block text-muted-foreground hover:text-foreground"
                to={`/resort/${p.slug}`}
                data-testid={`footer-project-${p.slug}`}
              >
                {p.short_name || p.name}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => openQualificaGuarded('footer_prenota')}
              className="block text-left w-full text-muted-foreground hover:text-foreground pt-1.5"
              data-testid="footer-prenota-call"
            >
              {t('landing.footer.bookCall', 'Prenota una call')}
            </button>
            <Link className="block text-muted-foreground hover:text-foreground" to="/login">{t('landing.footer.teamArea', 'Area team')}</Link>
            <button
              type="button"
              onClick={() => openQualificaGuarded('footer')}
              className="block text-left w-full text-[var(--gp-gold)] hover:opacity-80 font-medium"
              data-testid="footer-qualifica-cta"
            >
              {t('landing.footer.talkSenior', 'Parla con un Senior Associate →')}
            </button>
          </div>

          <div className="text-xs space-y-1.5">
            <div className="font-medium text-foreground mb-2 uppercase tracking-wider text-[10px]">{t('landing.footer.section.hcfGroup', 'HCF Group')}</div>
            <a className="block text-muted-foreground hover:text-foreground" href={SOCIALS.hcf_main} target="_blank" rel="noreferrer">housecashflows.com</a>
            <a className="block text-muted-foreground hover:text-foreground" href={SOCIALS.green_paradise_page} target="_blank" rel="noreferrer">Green Paradise</a>
            <a className="block text-muted-foreground hover:text-foreground" href={SOCIALS.lux_harmony_page} target="_blank" rel="noreferrer">Lux Harmony</a>
            <a className="block text-muted-foreground hover:text-foreground" href={SOCIALS.linkedin} target="_blank" rel="noreferrer">LinkedIn HCF Group</a>
          </div>

          <div className="text-xs">
            <div className="font-medium text-foreground mb-2 uppercase tracking-wider text-[10px]">{t('landing.footer.section.social', 'Social')}</div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Green Paradise</div>
                <div className="flex gap-2">
                  <a href={SOCIALS.ig_greenparadise} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="ig-greenparadise" title="Instagram Green Paradise"><Instagram className="w-3.5 h-3.5" /></a>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">HCF Property</div>
                <div className="flex gap-2">
                  <a href={SOCIALS.ig_property} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="ig-property"><Instagram className="w-3.5 h-3.5" /></a>
                  <a href={SOCIALS.yt_property} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="yt-property"><Youtube className="w-3.5 h-3.5" /></a>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">HCF Estate</div>
                <div className="flex gap-2">
                  <a href={SOCIALS.ig_estate} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="ig-estate"><Instagram className="w-3.5 h-3.5" /></a>
                  <a href={SOCIALS.yt_estate} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="yt-estate"><Youtube className="w-3.5 h-3.5" /></a>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">HCF Group</div>
                <div className="flex gap-2">
                  <a href={SOCIALS.yt_group} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="yt-group"><Youtube className="w-3.5 h-3.5" /></a>
                  <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-border grid place-items-center hover:bg-[hsl(var(--accent))]" data-testid="linkedin-group"><Linkedin className="w-3.5 h-3.5" /></a>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Customer Care — Claudia (post-vendita & supporto) */}
        <div className="max-w-6xl mx-auto pt-8 mt-8 border-t border-border" data-testid="footer-customer-care">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 items-center bg-[var(--surface-2)] rounded-xl p-4 md:p-5 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[var(--gp-gold)]/15 grid place-items-center text-[var(--gp-gold)]">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--gp-gold)] font-semibold">{t('landing.footer.customerCare.badge', 'Customer Care')}</div>
                <div className="font-serif text-base text-[hsl(var(--primary))] leading-tight mt-0.5">Claudia Lucantoni</div>
                <div className="text-[11px] text-muted-foreground">{t('landing.footer.customerCare.role', 'Assistenza clienti · Community · Post-vendita')}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed md:px-4">
              {t('landing.footer.customerCare.desc', 'Per supporto, informazioni operative e domande sui tuoi documenti. Claudia ti risponde su WhatsApp o Telegram.')}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <WhatsAppButton
                variant="solid"
                size="sm"
                label={t('landing.footer.customerCare.wa', 'WhatsApp Claudia')}
                phone="393519801113"
                source="footer_claudia"
                text="Ciao Claudia! Ho una domanda su Green Paradise."
              />
              <a
                href="https://t.me/Claudia_HCFGroup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 font-semibold tracking-[0.04em] h-9 px-3 text-[12px] rounded-full bg-[#229ED9] text-white hover:bg-[#1E8FC2] shadow-lg hover:shadow-xl transition-colors"
                data-testid="telegram-cta-footer-claudia"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{t('landing.footer.customerCare.tg', 'Telegram Claudia')}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto pt-8 mt-8 border-t border-border text-[10px] text-muted-foreground flex justify-between flex-wrap gap-2">
          <div>{t('landing.footer.copyright', '© {{year}} HCF Group / HCF Property — Tutti i diritti riservati', { year: new Date().getFullYear() })}</div>
          <div>{t('landing.footer.pipeline', 'Pipeline: La Mar Residence · Narea · nuovi progetti in arrivo')}</div>
        </div>
      </footer>

      {/* Video player modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4" onClick={() => setActiveVideo(null)} data-testid="video-modal">
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center text-white mb-3">
              <div className="font-serif text-lg">{activeVideo.title}</div>
              <button onClick={() => setActiveVideo(null)} className="text-white text-2xl leading-none hover:opacity-70" data-testid="video-modal-close">×</button>
            </div>
            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black shadow-2xl">
              <iframe
                title={activeVideo.title}
                src={activeVideo.url}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                data-testid="video-modal-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile sticky CTA bar */}
      <MobileStickyCTA />
    </div>
  );
}
